import React from 'react';
import { Helmet } from 'react-helmet-async';

// Base site configuration
const SITE_CONFIG = {
  siteName: 'Implify',
  siteUrl: 'https://implifyai.com',
  defaultTitle: 'Implify - AI Teammates for Modern Teams',
  defaultDescription: 'Meet your AI teammates. Implify provides specialized AI agents for marketing, sales, finance, HR, research, and more. Get work done faster with AI that understands your business.',
  defaultImage: 'https://implifyai.com/og-image.png',
  twitterHandle: '@implifyai',
  locale: 'en_US',
};

// Agent-specific SEO data
const AGENT_SEO_DATA = {
  buzz: {
    title: 'Buzz - AI Social Media Manager | Implify AI',
    description: 'Meet Buzz, your AI social media expert. Create engaging content, plan campaigns, analyze performance, and grow your social presence with AI-powered assistance.',
    keywords: 'AI social media manager, social media automation, content creation AI, social media marketing, AI marketing assistant',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  dora: {
    title: 'Dora - AI Research & Data Analyst | Implify AI',
    description: 'Meet Dora, your AI research analyst. Get comprehensive market research, competitor analysis, and data-driven insights to make smarter business decisions.',
    keywords: 'AI research analyst, market research AI, competitor analysis, data analysis AI, business intelligence',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  finn: {
    title: 'Finn - AI Finance Expert | Implify AI',
    description: 'Meet Finn, your AI finance expert. Track expenses, monitor cash flow, create financial reports, and get insights to improve your business financial health.',
    keywords: 'AI finance assistant, financial analysis AI, expense tracking, cash flow management, AI accounting',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'FinanceApplication',
  },
  luc: {
    title: 'Luc - AI Personal Growth Assistant | Implify AI',
    description: 'Meet Luc, your AI personal growth partner. Stay organized, manage tasks, plan your day, and maintain focus with intelligent productivity assistance.',
    keywords: 'AI personal assistant, productivity AI, task management, personal growth, AI organization',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'ProductivityApplication',
  },
  marsi: {
    title: 'Marsi - AI Marketing Strategist | Implify AI',
    description: 'Meet Marsi, your AI marketing strategist. Plan campaigns, develop marketing strategies, create compelling messaging, and drive business growth.',
    keywords: 'AI marketing strategist, marketing automation, campaign planning, AI marketing, growth marketing',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  sally: {
    title: 'Sally - AI Sales Development Expert | Implify AI',
    description: 'Meet Sally, your AI sales expert. Manage leads, track deals, automate follow-ups, and close more sales with intelligent pipeline management.',
    keywords: 'AI sales assistant, sales automation, lead management, CRM AI, sales pipeline',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  stark: {
    title: 'Stark - AI Strategy Advisor | Implify AI',
    description: 'Meet Stark, your AI strategy advisor. Get strategic guidance, pressure-test decisions, and align execution with clear business priorities.',
    keywords: 'AI strategy advisor, business strategy, decision making AI, strategic planning, executive AI',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  tali: {
    title: 'Tali - AI HR & People Partner | Implify AI',
    description: 'Meet Tali, your AI HR partner. Streamline hiring, onboarding, and people operations with intelligent HR workflow management.',
    keywords: 'AI HR assistant, HR automation, hiring AI, onboarding, people operations',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  wright: {
    title: 'Wright - AI Copywriter | Implify AI',
    description: 'Meet Wright, your AI copywriter. Create compelling copy for landing pages, emails, ads, and more with consistent brand voice and messaging.',
    keywords: 'AI copywriter, copywriting AI, content writing, marketing copy, AI writing assistant',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
  zira: {
    title: 'Zira - AI Customer Support Expert | Implify AI',
    description: 'Meet Zira, your AI support expert. Deliver fast, consistent customer support, manage conversations, and improve customer experience at scale.',
    keywords: 'AI customer support, support automation, customer service AI, help desk AI, customer experience',
    schemaType: 'SoftwareApplication',
    applicationCategory: 'BusinessApplication',
  },
};

// Page-specific SEO data
const PAGE_SEO_DATA = {
  home: {
    title: 'Implify - AI Teammates for Modern Teams',
    description: 'Meet your AI teammates. Implify provides specialized AI agents for marketing, sales, finance, HR, research, and more. Get work done faster with AI that understands your business.',
    keywords: 'AI teammates, AI agents, business automation, AI assistants, team productivity, AI for business',
  },
  pricing: {
    title: 'Pricing - Implify AI Teammates',
    description: 'Simple, transparent pricing for Implify AI teammates. Choose the plan that fits your team size and start working with AI agents today.',
    keywords: 'Implify pricing, AI assistant pricing, business AI cost, AI teammates plans',
  },
  howItWorks: {
    title: 'How It Works — the system behind your AI team | Implify',
    description: 'The system behind your AI team: multi-agent orchestration, native integrations, human-in-the-loop control, persistent memory, and the right model for every job. See how Implify’s AI agents work together on the tools you already use.',
    keywords: 'how Implify works, AI orchestration, multi-agent workflows, human in the loop AI, AI integrations, persistent memory AI, multi-model AI',
  },
  useCases: {
    title: 'Use Cases — outcomes by team & workflow | Implify',
    description: 'Built for how your team works. Two ways in: see what Implify does for your department (Sales, Marketing, Personal Productivity) or explore a stitched workflow (CRM management, Pipeline Generation, Focus & flow) and start it.',
    keywords: 'AI use cases, AI for sales, AI for marketing, CRM management AI, pipeline generation, AI productivity, business workflow automation',
  },
  services: {
    title: 'Services — AI agents set up for your business | Implify',
    description: 'Set up by us, run by your team. We design the strategy, configure a platform of AI agents to how your business works, and connect the tools you already use — your team runs it and approves anything customer-facing. Book a call to see what we’d set up.',
    keywords: 'done for you AI, AI agency, AI setup for business, AI agents configured, AI implementation, AI development agency, managed AI agents',
  },
  about: {
    title: 'About Us - Implify',
    description: 'Learn about Implify and our mission to build AI teammates that help modern teams work smarter. Discover how we build AI that teams can trust.',
    keywords: 'about Implify, AI company, AI teammates mission, trusted AI',
  },
  blog: {
    title: 'Blog - Implify',
    description: 'Insights, guides, and updates from the Implify team. Learn about AI productivity, business automation, and getting the most from your AI teammates.',
    keywords: 'Implify blog, AI insights, business automation blog, AI productivity tips',
  },
  faq: {
    title: 'FAQ — how Implify works | Implify',
    description: 'Answers to common questions about Implify: how the AI teammates work, agents and capabilities, integrations, workflows, security and privacy, pricing, and support.',
    keywords: 'Implify FAQ, AI teammates questions, how Implify works, AI agents help, AI automation FAQ',
  },
};

/**
 * SEO Component - Handles all meta tags, Open Graph, Twitter Cards, and JSON-LD
 */
export const SEO = ({ 
  page = 'home',
  agentSlug = null,
  customTitle = null,
  customDescription = null,
  customKeywords = null,
  customImage = null,
  canonicalUrl = null,
  noIndex = false,
  article = null, // For blog posts
  faqItems = null, // [{ question, answer }] → FAQPage schema (rich results + AI answers)
  breadcrumbs = null, // [{ name, url }] → BreadcrumbList schema
}) => {
  // Determine SEO data based on page type
  let seoData;
  if (agentSlug && AGENT_SEO_DATA[agentSlug]) {
    seoData = AGENT_SEO_DATA[agentSlug];
  } else if (PAGE_SEO_DATA[page]) {
    seoData = PAGE_SEO_DATA[page];
  } else {
    seoData = PAGE_SEO_DATA.home;
  }

  const title = customTitle || seoData.title;
  const description = customDescription || seoData.description;
  const image = customImage || SITE_CONFIG.defaultImage;
  const url = canonicalUrl || `${SITE_CONFIG.siteUrl}${window.location.pathname}`;
  const keywords = customKeywords || seoData.keywords || '';

  // Build JSON-LD structured data
  const getStructuredData = () => {
    const baseOrganization = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Implify',
      url: SITE_CONFIG.siteUrl,
      logo: `${SITE_CONFIG.siteUrl}/logo.png`,
      sameAs: [
        'https://x.com/implifyai',
        'https://www.linkedin.com/company/implifyai',
        'https://www.instagram.com/implify.ai',
      ],
      description: SITE_CONFIG.defaultDescription,
    };

    const schemas = [baseOrganization];

    // Page-type primary schema
    if (agentSlug && AGENT_SEO_DATA[agentSlug]) {
      const agentData = AGENT_SEO_DATA[agentSlug];
      schemas.push({
        '@context': 'https://schema.org',
        '@type': agentData.schemaType || 'SoftwareApplication',
        name: title.split(' - ')[0], // Just the agent name
        applicationCategory: agentData.applicationCategory,
        operatingSystem: 'Web',
        description: description,
        url: url,
        provider: {
          '@type': 'Organization',
          name: 'Implify',
          url: SITE_CONFIG.siteUrl,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
        },
      });
    } else if (article) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        image: image,
        url: url,
        datePublished: article.datePublished,
        dateModified: article.dateModified || article.datePublished,
        author: {
          '@type': 'Organization',
          name: 'Implify',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Implify',
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_CONFIG.siteUrl}/logo.png`,
          },
        },
      });
    } else if (page === 'home') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Implify',
        url: SITE_CONFIG.siteUrl,
        description: SITE_CONFIG.defaultDescription,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_CONFIG.siteUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      });
    }

    // BreadcrumbList (e.g. Use Cases sub-pages) — helps both search and AI parse hierarchy
    if (breadcrumbs && breadcrumbs.length) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.url,
        })),
      });
    }

    // FAQPage (the FAQ page) — high-value for rich results + LLM answer extraction
    if (faqItems && faqItems.length) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      });
    }

    return schemas;
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Implify" />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SITE_CONFIG.twitterHandle} />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#FF6B6B" />
      <meta name="msapplication-TileColor" content="#FF6B6B" />
      <meta name="application-name" content="Implify" />
      <meta name="apple-mobile-web-app-title" content="Implify" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};

// Export agent SEO data for external use
export const getAgentSEO = (agentSlug) => AGENT_SEO_DATA[agentSlug] || null;
export const getPageSEO = (page) => PAGE_SEO_DATA[page] || PAGE_SEO_DATA.home;

export default SEO;
